import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text, Textarea, CopyButton, Button, Group, Anchor, Switch, Paper, ThemeIcon, Stack, Code } from '@mantine/core';
import { IconX, IconDownload, IconPuzzle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import SettingsApi from '../../api/SettingsApi';
import { HelpTip } from './GeneralComponent';

// Universal smart code data returned by get_universalsmart_code, in one shape for all consumers.
export interface UniversalSmartCodeData {
  visiSmartCode: string;
  visiScriptID: string;
  frontendFramework: string;
  frontendFrameworktype: string;
  trackingUrl: string;
  nextJsSmartCode: string;
  wpPluginUrl: string;
  emdashPluginUrl: string;
  ghlCSS: string;
  visiSmartForShopifyCode: string;
}

const DEFAULT_DATA: UniversalSmartCodeData = {
  visiSmartCode: '',
  visiScriptID: '',
  frontendFramework: '0',
  frontendFrameworktype: '0',
  trackingUrl: '',
  nextJsSmartCode: '',
  wpPluginUrl: '',
  emdashPluginUrl: '',
  ghlCSS: '',
  visiSmartForShopifyCode: '',
};

interface UseUniversalSmartCodeParams {
  websiteId: string | number;
  isSubdomain?: boolean;
  subdomainId?: string | number;
  pageId?: string;
  enabled?: boolean;
}

// Single source for fetching the universal smart code, shared by every modal that displays it.
export function useUniversalSmartCode({ websiteId, isSubdomain = false, subdomainId = '', pageId = '', enabled = true }: UseUniversalSmartCodeParams) {
  const navigate = useNavigate();
  const [data, setData] = useState<UniversalSmartCodeData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  const reload = () => setReloadCount((c) => c + 1);

  useEffect(() => {
    if (!enabled || !websiteId) return;
    setData((d) => ({ ...d, visiSmartCode: '' }));
    setLoading(true);
    SettingsApi.getuniversalsmartcode({
      website_id: websiteId,
      is_subdomain: isSubdomain,
      subdomain_id: subdomainId,
      page_id: pageId,
    })
    .then((response) => {
      if (response.ok) {
        return response.json();
      } else if (response.status === 401 || response.status === 400) {
        navigate('/logout');
        setLoading(false);
        return null;
      }
    })
    .then((res) => {
      if (res && res.status === 1) {
        setData({
          visiSmartCode: res.data.tracking_code || '',
          visiScriptID: res.data.tracking_script_id || '',
          frontendFramework: res.data.front_end_framework || '0',
          frontendFrameworktype: res.data.frontend_framework_type || '0',
          trackingUrl: res.data.tracking_url || '',
          nextJsSmartCode: res.data.nextjs_tracking_code || '',
          wpPluginUrl: res.data.wp_plugin_url,
          emdashPluginUrl: res.data.emdash_plugin_url,
          ghlCSS: res.data.ghl_css,
          visiSmartForShopifyCode: res.data.shopify_thankyou_code || '',
        });
      } else if (res) {
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: res.message || 'Failed to load tracking code',
        });
      }
      setLoading(false);
    })
    .catch((error) => {
      setLoading(false);
      console.error('API Error:', error);
      notifications.show({
        color: 'red',
        icon: <IconX size="1.1rem" />,
        title: 'Error',
        message: 'Failed to load tracking code',
      });
    });
  }, [enabled, websiteId, pageId, reloadCount]);

  return { data, loading, reload };
}

// Shared framework-aware universal smart code body (GHL, WordPress, frontend framework, Shopify, Next.js).
interface UniversalSmartCodeContentProps {
  data: UniversalSmartCodeData;
  showShopifyConfirmationCode?: boolean;
}

export function UniversalSmartCodeContent({
  data,
  showShopifyConfirmationCode = true,
}: UniversalSmartCodeContentProps) {

  const {
    visiSmartCode,
    visiScriptID,
    frontendFramework,
    frontendFrameworktype,
    trackingUrl,
    nextJsSmartCode,
    wpPluginUrl,
    emdashPluginUrl,
    ghlCSS,
    visiSmartForShopifyCode,
  } = data;

  const [showShopifyCode, setShowShopifyCode] = useState(false);
  const [showShopifyThankyouCode, setShowShopifyThankyouCode] = useState(false);

  const ghlFooterCode = `<script src="https://cdn.jsdelivr.net/npm/vue@2"></script><script>new Vue({el: '#app', mounted() {if(typeof visi_do_replace==='function'){visi_do_replace();}}});</script>`;

  const isNextLayout = frontendFramework === "1" && frontendFrameworktype === "4";
  const isAstro = frontendFramework === "1" && frontendFrameworktype === "6";

  // Astro loads the code globally by injecting the raw JS body via injectScript
  const astroSmartCode = `export default defineConfig({
  integrations: [
    {
      name: "visiopt-smartcode",
      hooks: {
        "astro:config:setup": ({ injectScript }) => {
          injectScript("head-inline", \`${nextJsSmartCode}\`);
        },
      },
    },
  ],
});`;

  const astroPerformanceCode = `<link rel="preconnect" href="${trackingUrl}" />
<link rel="dns-prefetch" href="${trackingUrl}" />`;

  const visiUniversalSmartCode = isNextLayout
? `<link rel="preconnect" href="${trackingUrl}" />
<link rel="dns-prefetch" href="${trackingUrl}" />

<Script
  id="visiopt-script"
  strategy="afterInteractive"
  dangerouslySetInnerHTML={{
    __html: \`${nextJsSmartCode}\`
  }}
/>`
  : isAstro
? astroSmartCode
  : visiSmartCode;

  return (
    <>
      {/*GHL CSS*/}
      {frontendFramework === "3" && (
        <>
          <Text fw={700} size="sm" ml={0} fz={14}>GHL CSS <HelpTip helpId={363} /> </Text>
          <Text size="sm" ml={0} fz={14} mb={5}>Copy/Paste this CSS snippet to the Custom CSS section of your GHL page: </Text>
          <Group align="flex-start" spacing="md">
            <Textarea
              mb={10}
              value={ghlCSS}
              size="xs"
              autosize
              w={350}
            />
            <CopyButton value={ghlCSS} timeout={5000}>
              {({ copied, copy }) => (
              <Button
                color={copied ? 'teal' : 'blue'}
                onClick={copy}
                size="sm"
                w={120}
                mt={0}
              >
                {copied ? 'CSS Copied' : 'Copy CSS'}
              </Button>
              )}
            </CopyButton>
          </Group>
        </>
      )}
      {/*Tracking Script ID Text*/}
      {(frontendFramework === "3") ? (
        <>
          <Text fw={700} size="sm" fz={14} mb={6}>VisiSmart Code <HelpTip helpId={370} /></Text>
          <Text size="xs" color="dimmed" mb="xs" style={{ lineHeight: 1.5 }}>
            Place the following script on your page just before the closing <code>&lt;/head&gt;</code> tag.<br />
            <strong>Recommended:</strong> Install this code once in your website's <strong>global/site-wide header settings</strong> whenever available. Avoid adding it separately to individual pages in page builders.<br />
            <strong>Note:</strong> Use this same code site-wide.
          </Text>
        </>
      ) : isAstro ? (
        <>
          <Group spacing={4} mb={10}>
            <Text size="sm" fz={14}>
              Tracking Site ID:
            </Text>
            <Text size="sm" fz={14} fw={700}>
              {visiScriptID}
            </Text>
          </Group>
          <Text size="xs" color="dimmed" mb="xs" style={{ lineHeight: 1.5 }}>
            <strong>Step 1:</strong> Open your <strong><code>astro.config.mjs</code></strong> file (or <strong><code>astro.config.ts</code></strong> if applicable).<br />
            <strong>Step 2:</strong> Add the following integration inside <code>defineConfig()</code> so the Smart Code is injected globally on every page.
          </Text>
        </>
      ) : (frontendFramework === "1" && frontendFrameworktype !== "4") ? (
        <>
          <Group spacing={4} mb={10}>
            <Text size="sm" fz={14}>
              Tracking Site ID:
            </Text>
            <Text size="sm" fz={14} fw={700}>
              {visiScriptID}
            </Text>
          </Group>
          {(frontendFramework === "1" && frontendFrameworktype !== "5") && (
            <Text size="xs" color="dimmed" mb="xs" style={{ lineHeight: 1.5 }}>
              Place the following script inside your project's <strong>index.html</strong> file just before the closing <code>&lt;/head&gt;</code> tag.<br />
              <strong>Recommended:</strong> Install this code once in your website's <strong>global/site-wide header settings</strong> whenever available. Avoid adding it separately to individual pages in page builders.<br />
              <strong>Note:</strong> Use this same code site-wide.
            </Text>
          )}
        </>
      ) : (frontendFramework === "1" && frontendFrameworktype === "4") ? (
        <>
          <Group spacing={4} mb={10}>
            <Text size="sm" fz={14}>
              Tracking Site ID:
            </Text>
            <Text size="sm" fz={14} fw={700}>
              {visiScriptID}
            </Text>
          </Group>
          <Text size="xs" color="dimmed" mb="xs" style={{ lineHeight: 1.5 }}>
            Place the following script inside your project's <strong>layout.tsx</strong> file just before the closing <code>&lt;/head&gt;</code> tag.<br />
            <strong>Important:</strong> If you haven't already, make sure to add <strong><code>import Script from "next/script";</code></strong> at the top of your <strong>layout.tsx</strong> file — this is required for the script below to work.<br />
            <strong>Note:</strong> Use this same code site-wide.
          </Text>
        </>
      ) : frontendFramework !== "2" && (
        <>
          <Group spacing={4} mb={10}>
            <Text size="sm" fz={14}>
              Tracking Site ID:
            </Text>
            <Text size="sm" fz={14} fw={700}>
              {visiScriptID}
            </Text>
          </Group>
          <Text size="xs" color="dimmed" mb="xs" style={{ lineHeight: 1.5 }}>
            Place the following script on your page just before the closing <code>&lt;/head&gt;</code> tag.<br />
            <strong>Recommended:</strong> Install this code once in your website's <strong>global/site-wide header settings</strong> whenever available. Avoid adding it separately to individual pages in page builders.<br />
            <strong>Note:</strong> Use this same code site-wide.
          </Text>
        </>
      )}
      {frontendFramework === "2" && (
        <>
          <Text size="md" fz={14} mb={6}>
            If you have set up your Shopify domain properly in the domain settings, you should be all set.
            Please note that in order to track properly, you need to add a confirmation page and a webhook.
          </Text>
          <Text size="md" fz={14} mb={12}>
            Click this{" "}
            <Anchor
              href="https://visiopt.com/help-videos/Shopify%20Thank%20You%20and%20Webhook.pdf"
              target="_blank"
              underline
              c="blue"
              fw={600}
            >
              link
            </Anchor>{" "}
            for more info.
          </Text>
          {showShopifyConfirmationCode && (
            <>
              <Text size="sm" fz={14} mb={4}>
                Toggle this ON only if this page is your Shopify order confirmation/thank-you page.
              </Text>
              <Text size="sm" fz={14} mb={12}>
                Leave it OFF for all other pages.
              </Text>
              <Group spacing="xs" ml={0} mb={10}>
                <Text size="sm" fz={14} fw={500} mb={10} w={250} ml={0} mr={-10}>This is my Shopify confirmation page </Text>
                <Switch
                  size="md"
                  onLabel="ON"
                  offLabel="OFF"
                  style={{ marginBottom: 4 }}
                  checked={showShopifyThankyouCode}
                  onChange={(event) => setShowShopifyThankyouCode(event.currentTarget.checked)}
                />
              </Group>
              {showShopifyThankyouCode && (
                <>
                  <Text size="sm" ml={0} fz={14} mb={10}>Please Copy the below code and paste it into Shopify Admin → Customer Events → Add New Custom Pixel</Text>
                  <Textarea
                    value={visiSmartForShopifyCode}
                    size="xs"
                    autosize
                  />
                  <CopyButton value={visiSmartForShopifyCode} timeout={5000}>
                    {({ copied, copy }) => (
                      <Button color={copied ? 'teal' : 'blue'} onClick={copy} size="sm" w={260} mt={10} mb={10}>
                        {copied ? 'VisiSmart Code for Shopify Copied' : 'Copy VisiSmart Code for Shopify'}
                      </Button>
                    )}
                  </CopyButton>
                </>
              )}
            </>
          )}
          <Group spacing="xs" ml={0} mb={10}>
            <Text size="sm" fz={14} mb={10} ml={0}>Turn this <strong>ON</strong> if this page is not built or hosted on Shopify. </Text>
            <Switch
              size="md"
              onLabel="ON"
              offLabel="OFF"
              style={{ marginBottom: 8 }}
              checked={showShopifyCode}
              onChange={(event) => setShowShopifyCode(event.currentTarget.checked)}
            />
          </Group>
          {showShopifyCode && (
            <>
              <Textarea
                value={visiSmartCode}
                size="xs"
                autosize
              />
              <CopyButton value={visiSmartCode} timeout={5000}>
                {({ copied, copy }) => (
                  <Button color={copied ? 'teal' : 'blue'} onClick={copy} size="sm" w={260} mt={10} mb={10}>
                    {copied ? 'VisiSmart Code for Shopify Copied' : 'Copy VisiSmart Code for Shopify'}
                  </Button>
                )}
              </CopyButton>
            </>
          )}
        </>
      )}
      {frontendFramework !== "2" && (
        <>
          <Textarea
            value={visiUniversalSmartCode}
            size="xs"
            autosize
          />
          <CopyButton value={visiUniversalSmartCode} timeout={5000}>
            {({ copied, copy }) => (
              <Button color={copied ? 'teal' : 'blue'} onClick={copy} size="sm" w={230} mt={10} mb={10}>
                {copied ? 'Universal Smart Code Copied' : ' Copy Universal Smart Code'}
              </Button>
            )}
          </CopyButton>
        </>
      )}
      {/*Astro extra notes and optional performance optimization*/}
      {isAstro && (
        <>
          <Text size="xs" color="dimmed" mb="md" style={{ lineHeight: 1.5 }}>
            This will inject the VisiOpt Universal Smart Code into the <code>&lt;head&gt;</code> of every page in your Astro application. No additional changes are required.<br />
            <strong>Note:</strong> Restart your Astro development server after saving the config.
          </Text>
          <Text fw={700} size="sm" fz={14} mb={4}>Optional Performance Optimization</Text>
          <Text size="xs" color="dimmed" mb="xs" style={{ lineHeight: 1.5 }}>
            You can optionally add the following tags inside your site's <code>&lt;head&gt;</code>:
          </Text>
          <Textarea
            value={astroPerformanceCode}
            size="xs"
            autosize
          />
          <CopyButton value={astroPerformanceCode} timeout={5000}>
            {({ copied, copy }) => (
              <Button color={copied ? 'teal' : 'blue'} onClick={copy} size="sm" w={230} mt={10} mb={10}>
                {copied ? 'Optimization Tags Copied' : 'Copy Optimization Tags'}
              </Button>
            )}
          </CopyButton>
          <Text size="xs" color="dimmed" mb="xs" style={{ lineHeight: 1.5 }}>
            These tags are optional and only improve connection performance. The Universal Smart Code works correctly even if they are not added.
          </Text>
        </>
      )}
      {/*Wordpress Link*/}
      {frontendFramework === "4" && (
        <>
          <Text size="sm" ml={0} fz={14}>Using a WordPress site? Install our WordPress plugin for easy integration.</Text>
          <div style={{ display: 'flex',marginBottom: '10px' }}>
            <Text size="sm" fz={14}>You can download the plug in from this
              <Anchor href={wpPluginUrl} target="_blank" ml={5} fw={700}>
              LINK.
              </Anchor>
            </Text>
          </div>
        </>
      )}
      {/*EmDash CMS Plugin instructions*/}
      {frontendFramework === "5" && (
        <Paper
          withBorder
          radius="md"
          p="md"
          mt="md"
          sx={(theme) => ({
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : '#f8fafc',
          })}
        >
          <Group spacing={10} mb={4} noWrap>
            <ThemeIcon color="blue" variant="light" radius="xl" size={30}>
              <IconPuzzle size="1.1rem" />
            </ThemeIcon>
            <Text fw={700} size="sm" fz={15}>Using an EmDash CMS site?</Text>
          </Group>
          <Text size="sm" color="dimmed" fz={13} mb="sm">
            Install our plugin for automatic site-wide integration — no manual code needed.
          </Text>
          <Button
            component="a"
            href={emdashPluginUrl}
            target="_blank"
            leftIcon={<IconDownload size="1rem" />}
            size="sm"
            mb="md"
          >
            Download Plugin
          </Button>
          <Stack spacing="sm">
            {[
              <>Unzip <Code>emdash-visiopt-sitewide</Code> into your EmDash project root.</>,
              <>From the project root run <Code>node ./emdash-visiopt-sitewide/install.mjs</Code>, then restart the server (or rebuild).</>,
              <>Open the EmDash admin &rarr; <strong>Visiopt</strong>, enter your Tracking Site ID (<strong>{visiScriptID}</strong>), and click <strong>Save &amp; Sync</strong>.</>,
            ].map((step, index) => (
              <Group key={index} spacing="sm" align="flex-start" noWrap>
                <ThemeIcon color="blue" radius="xl" size={22} style={{ flexShrink: 0, marginTop: 1 }}>
                  <Text fz={11} fw={700}>{index + 1}</Text>
                </ThemeIcon>
                <Text size="sm" fz={14} style={{ lineHeight: 1.5 }}>{step}</Text>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}
      {/*GHL Footer Code*/}
      {frontendFramework === "3" && (
        <>
          <Text fw={700} size="sm" fz={14} mb={6}>
            Footer Code <HelpTip helpId={371} />
          </Text>
          <Text size="sm" fz={14} mb={2}>
            (Only required the first time you set up your site.)
          </Text>
          <Text size="sm" fz={14} mb={8}>
            Paste the following code into the footer of your site.
          </Text>
          <Textarea
            value={ghlFooterCode}
            size="xs"
            autosize
          />
          <CopyButton value={ghlFooterCode} timeout={5000}>
            {({ copied, copy }) => (
              <Button color={copied ? 'teal' : 'blue'} onClick={copy} size="sm" w={260} mt={10} mb={10}>
                {copied ? 'Footer Code for GHL Copied' : 'Copy Footer Code for GHL'}
              </Button>
            )}
          </CopyButton>
        </>
      )}
    </>
  );
}
